import { FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { useTimeout } from "~/hooks";
import { randInt } from "~/utils";
import { Point, ProcessAnimationProps } from "./interfaces";

const spread = (
    width: number,
    height: number,
    dotCount: number,
    minDist: number,
    offset: Point = { x: 0, y: 0 },
) => {
    const maxTries = 1000;
    const points: Point[] = [];

    for (let tries = 0; points.length < dotCount && tries < maxTries; tries++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        let tooClose = false;

        for (const { x: dx, y: dy } of points) {
            const dist = Math.hypot(dx - x, dy - y);

            if (dist < minDist) {
                tooClose = true;
                break;
            }
        }

        if (!tooClose) {
            points.push({ x, y });
        }
    }

    points.forEach((dot) => {
        dot.x += offset.x;
        dot.y += offset.y;
    });

    return points;
};

const generateShapes = (parent: SVGElement, w: number, h: number) => {
    const shapes: SVGElement[] = [];
    const padding = Math.max(w, h) * 0.05;
    const fw = w - padding * 2;
    const fh = h - padding * 2;
    const points = spread(fw, fh, 4, Math.max(fw, fh) * 0.1, {
        x: padding,
        y: padding,
    });

    for (const { x, y } of points) {
        const shape = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "rect",
        );
        const iw = randInt(50, 100);
        const ih = iw * 0.6;

        shape.setAttribute("x", String(x));
        shape.setAttribute("y", String(y));
        shape.setAttribute("width", String(iw));
        shape.setAttribute("height", String(ih));
        shape.setAttribute("fill", "#fff");
        shape.setAttribute("stroke", "#fff");
        shape.setAttribute("fill-opacity", "0.6");
        shape.setAttribute("stroke-width", "2");

        parent.appendChild(shape);

        shapes.push(shape);
    }

    return shapes;
};

const ProcessAnimation: FC<ProcessAnimationProps> = ({
    active,
    imgSize,
    offset,
}) => {
    const visible = useSignal(active);
    const delayStart = useTimeout(300);
    const ref = useRef<SVGSVGElement>(null);
    const shapes = useRef<SVGElement[]>([]);

    useEffect(() => {
        // delay the animation to avoid flickering when process has been completed but not yet fetched
        //
        if (!active) {
            visible.value = false;
            delayStart(false);
        } else {
            delayStart(() => {
                visible.value = true;
            });
        }
    }, [active, visible, delayStart]);

    useEffect(() => {
        const svg = ref.current;
        const container = svg?.querySelector<SVGElement>(
            "[data-name='shape-container']",
        );

        if (!svg || !container || !active) {
            return;
        }

        svg.querySelectorAll<SVGAnimateElement>("animate").forEach((el) => {
            el.beginElement();
        });

        const w = imgSize.width;
        const h = imgSize.height;

        shapes.current.forEach((shape) => shape.remove());
        shapes.current = generateShapes(container, w, h);

        return () => {
            shapes.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible.value]);

    if (!visible.value) {
        return null;
    }

    const th = imgSize.height * 0.24;

    return (
        <svg
            ref={ref}
            style={{
                width: imgSize.width,
                height: imgSize.height,
                top: offset.y,
                left: offset.x,
            }}
            viewBox={`0 0 ${imgSize.width} ${imgSize.height}`}
            className="pointer-events-none absolute z-10 opacity-40"
        >
            <rect
                x="0"
                y="0"
                width="100%"
                height={th}
                fill="url(#pa-lg-trace)"
                filter="url(#pa-f-trace-glow)"
            >
                <animate
                    attributeName="y"
                    values={`0; ${imgSize.height - th}; 0`}
                    dur="5s"
                    repeatCount="indefinite"
                    begin="indefinite"
                    keyTimes="0; 0.5; 1"
                    keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                    calcMode="spline"
                    restart="always"
                />
            </rect>
            <g data-name="shape-container" mask="url(#pa-clip)" />
            <rect
                data-name="line"
                x="0"
                y={th / 2}
                width="100%"
                height="8"
                fill="#24d135"
                fillOpacity="0.8"
                className="mix-blend-color-burn"
            >
                <animate
                    attributeName="y"
                    values={`${th / 2}; ${imgSize.height - th / 2}; ${th / 2}`}
                    dur="5s"
                    repeatCount="indefinite"
                    begin="indefinite"
                    keyTimes="0; 0.5; 1"
                    keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                    calcMode="spline"
                    restart="always"
                />
            </rect>
            <defs>
                <mask id="pa-clip">
                    <rect
                        x="0"
                        y="0"
                        width="100%"
                        height={th}
                        fill="url(#pa-lg-trace-mask)"
                    >
                        <animate
                            attributeName="y"
                            values={`0; ${imgSize.height - th}; 0`}
                            dur="5s"
                            repeatCount="indefinite"
                            begin="indefinite"
                            keyTimes="0; 0.5; 1"
                            keySplines="0.42 0 0.58 1; 0.42 0 0.58 1"
                            calcMode="spline"
                            restart="always"
                        />
                    </rect>
                </mask>
                <filter id="pa-f-trace-glow">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                    <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <linearGradient
                    id="pa-lg-trace"
                    x1="50%"
                    y1="0%"
                    x2="50%"
                    y2="100%"
                    gradientUnits="objectBoundingBox"
                >
                    <stop offset="0%" stopColor="rgba(36, 209, 53, 0)" />
                    <stop offset="20%" stopColor="rgba(36, 209, 53, 0.1)" />
                    <stop offset="40%" stopColor="rgba(36, 209, 53, 0.4)" />
                    <stop offset="50%" stopColor="rgba(36, 209, 53, 0.5)" />
                    <stop offset="60%" stopColor="rgba(36, 209, 53, 0.4)" />
                    <stop offset="80%" stopColor="rgba(36, 209, 53, 0.1)" />
                    <stop offset="100%" stopColor="rgba(36, 209, 53, 0)" />
                </linearGradient>
                <linearGradient
                    id="pa-lg-trace-mask"
                    x1="50%"
                    y1="0%"
                    x2="50%"
                    y2="100%"
                    gradientUnits="objectBoundingBox"
                >
                    <stop offset="0%" stopColor="rgba(36, 209, 53, 0)" />
                    <stop offset="20%" stopColor="rgba(36, 209, 53, 0.4)" />
                    <stop offset="40%" stopColor="rgba(36, 209, 53, 0.8)" />
                    <stop offset="50%" stopColor="rgba(36, 209, 53, 0.9)" />
                    <stop offset="60%" stopColor="rgba(36, 209, 53, 0.8)" />
                    <stop offset="80%" stopColor="rgba(36, 209, 53, 0.4)" />
                    <stop offset="100%" stopColor="rgba(36, 209, 53, 0)" />
                </linearGradient>
            </defs>
        </svg>
    );
};

export default ProcessAnimation;
